/**
 * Componente para mostrar la lista de subcategorías.
 *
 * Este componente renderiza una tabla con todas las subcategorías de una categoría
 * específica, incluyendo funcionalidades de paginación, acciones de edición y
 * eliminación, y navegación de regreso a la lista de categorías.
 *
 * @author Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 * @date [Fecha de creación: 2025-08-06]
 * @updated [Fecha de última modificación: 2025-08-11]
 * @updatedBy Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 */

import { useState } from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PropTypes from "prop-types";
import { Pagination, Stack } from "@mui/material";
import usePermission from "hooks/usePermission";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import MDBadge from "components/MDBadge";
import { useMaterialUIController } from "context";
import colors from "assets/theme/base/colors"
export default function SubCategoryList({
  handleSubCategoryCreate,
  handleSubCategoryEdit,
  handleSubCategoryDelete,
  entriesStart,
  entriesEnd,
  totalData,
  totalPages,
  currentPage,
  handlePageChange,
  subCategoriesList = [],
  categoryName = "",
  handleGoBack,
}) {
  const canCreate = usePermission("Crear", "Subcategorías");
  const canEdit = usePermission("Editar", "Subcategorías");
  const canDelete = usePermission("Eliminar", "Subcategorías");
  const navigate = useNavigate();

  let { white, black, text } = colors

  black = black.main;
  white = white.main;
  const iconColor = text.main; // #7b809a - mismo color que el texto de las tablas

  // Obtener el estado del tema (modo oscuro/claro)
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  /**
   * Formatea una fecha para mostrarla en formato legible
   * @param {string} dateString - Fecha en formato string
   * @returns {string} Fecha formateada
   */
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    // El backend envía la fecha en hora local de Colombia
    // Solo necesitamos formatearla sin convertir zona horaria
    const dateStr = dateString.replace('T', ' ').split('.')[0];

    // Validar que la fecha tenga el formato esperado
    if (!/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}/.test(dateStr)) {
      return "Fecha inválida";
    }

    return dateStr;
  };

  const columns = [
    { Header: "Nombre", accessor: "name", align: "left" },
    { Header: "Estado", accessor: "isEnabled", align: "center" },
    { Header: "Creado", accessor: "created", align: "center" },
    { Header: "Modificado", accessor: "modified", align: "center" },
    { Header: "Creado por", accessor: "created_by", align: "center" },
    { Header: "Modificado por", accessor: "modified_by", align: "center" },
    { Header: "Acciones", accessor: "action", width: "12%", align: "center", disableSortBy: true },
  ];

  const rows = subCategoriesList.map((subcategory) => ({
    name: subcategory.name,
    isEnabled: (
      <MDBadge
        badgeContent={subcategory.isEnabled ? "Activo" : "Inactivo"}
        color={subcategory.isEnabled ? "success" : "error"}
        variant="gradient"
      />
    ),
    created: formatDate(subcategory.created),
    modified: formatDate(subcategory.modified),
    created_by: subcategory.created_by?.email || "N/A",
    modified_by: subcategory.modified_by?.email || "N/A",
    action: (
      <MDBox style={{ display: "flex", gap: "10px" }}>
        {canEdit && (
          <a onClick={() => handleSubCategoryEdit(subcategory)} style={{ cursor: "pointer" }}>
            <EditOutlinedIcon
              titleAccess="Editar"
              style={{
                fontSize: "24px",
                width: "24px",
                height: "24px",
                color: darkMode ? white : iconColor,
              }}
            />
          </a>
        )}
        {canDelete && (
          <a onClick={() => handleSubCategoryDelete(subcategory)} style={{ cursor: "pointer" }}>
            <DeleteOutlineIcon
              titleAccess="Eliminar"
              style={{
                fontSize: "24px",
                width: "24px",
                height: "24px",
                color: darkMode ? white : iconColor,
              }}
            />
          </a>
        )}
      </MDBox>
    ),
  }));

  return (
    <>
      {/* Header con título y botones */}
      <MDBox
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          marginRight: "2vw",
        }}>
        <MDBox style={{ display: "flex", alignItems: "center" }}>
          <MDButton
            onClick={handleGoBack}
            variant="outlined"
            color="info"
            style={{ marginRight: "10px", marginLeft: "20px", marginTop: "20px" }}>
            <ArrowBackIcon style={{ marginRight: "5px" }} />
            Volver
          </MDButton>
        </MDBox>
        {canCreate && (
          <MDButton onClick={handleSubCategoryCreate} variant="contained" color="info">
            Crear Subcategoría
          </MDButton>
        )}
      </MDBox>

      <DataTable
        table={{ columns, rows }}
        isSorted={true}
        entriesPerPage={false}
        showTotalEntries={false}
        canSearch={false}
        noEndBorder={false}
      />

      {!subCategoriesList || subCategoriesList.length === 0 ? (
        <MDBox style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <MDTypography
            variant="button"
            color="secondary"
            fontWeight="regular"
            style={{ fontSize: "20px", fontFamily: "Roboto, sans-serif" }}>
            No hay subcategorías para esta categoría
          </MDTypography>
        </MDBox>
      ) : (
        <>
          <MDBox style={{ marginLeft: "20px" }} mb={{ xs: 3, sm: 0 }}>
            <MDTypography
              style={{ left: "20px" }}
              variant="button"
              fontWeight="regular">
              Mostrando {entriesStart} hasta {entriesEnd} de {totalData} resultados en total
            </MDTypography>
          </MDBox>
          <Stack style={{ marginLeft: "20px", marginBottom: "10px" }} spacing={2}>
            <Pagination color={darkMode ? "white" : "standard"} count={totalPages} page={currentPage} onChange={handlePageChange} />
          </Stack>
        </>
      )}
    </>
  );
}

SubCategoryList.propTypes = {
  handleSubCategoryCreate: PropTypes.func.isRequired,
  handleSubCategoryEdit: PropTypes.func.isRequired,
  handleSubCategoryDelete: PropTypes.func.isRequired,
  entriesStart: PropTypes.number,
  entriesEnd: PropTypes.number,
  totalData: PropTypes.number,
  totalPages: PropTypes.number,
  currentPage: PropTypes.number,
  handlePageChange: PropTypes.func,
  subCategoriesList: PropTypes.array,
  categoryName: PropTypes.string,
  handleGoBack: PropTypes.func.isRequired,
};
